import { z } from 'zod';
import {
    workflowSchema,
    workflowAgentProposalResponseSchema,
    type Workflow,
    type WorkflowAgentProposalRequest,
    type WorkflowAgentProposalResponse,
    type WorkflowNode,
} from './schemas';

const modelProposalSchema = z.object({
    message: z.string().optional(),
    proposal: z.object({
        summary: z.string().optional(),
        workflow: workflowSchema,
        warnings: z.array(z.string()).optional(),
    }),
});

export type ModelProposal = z.infer<typeof modelProposalSchema>;

/**
 * Calls the model for one proposal attempt. `validationError` is the empty
 * string on the first attempt and the rejection reason on the repair attempt.
 * The model / provider stays out of this package — the server injects this.
 */
export type CallModel = (validationError: string) => Promise<string>;

/**
 * Two-attempt repair loop, provider-injected.
 *
 * First attempt runs with an empty validation error. If the model output
 * fails to parse / normalise, the failure reason is fed back into a second
 * call. If the second attempt also fails, throws with the last error.
 *
 * The "is the provider configured" guard (e.g. "Claude is not configured")
 * is the caller's job — pre-check before calling this.
 */
export async function proposeWorkflow(
    currentWorkflow: Workflow,
    callModel: CallModel,
): Promise<WorkflowAgentProposalResponse> {
    let validationError = '';
    for (let attempt = 0; attempt < 2; attempt += 1) {
        const raw = await callModel(validationError);
        try {
            const modelProposal = parseAgentModelOutput(raw);
            return buildProposalResponse(modelProposal, currentWorkflow);
        } catch (err) {
            validationError = err instanceof Error ? err.message : String(err);
        }
    }

    throw new Error(`The workflow agent returned an invalid workflow: ${validationError}`);
}

export function parseAgentModelOutput(raw: string): ModelProposal {
    const jsonText = extractJsonObject(raw);
    let parsed: unknown;
    try {
        parsed = JSON.parse(jsonText);
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        throw new Error(`Model output was not valid JSON: ${message}`);
    }
    return modelProposalSchema.parse(parsed);
}

export function buildProposalResponse(
    modelProposal: ModelProposal,
    currentWorkflow: Workflow,
): WorkflowAgentProposalResponse {
    const workflow = normalizeAgentWorkflow(modelProposal.proposal.workflow, currentWorkflow);
    const nodes = workflow.nodes.map((node) => ({
        id: node.id,
        kind: node.kind,
        label: node.data.label,
    }));
    const summary =
        modelProposal.proposal.summary?.trim() ||
        `Built a workflow with ${workflow.nodes.length} nodes and ${workflow.edges.length} edges.`;

    return workflowAgentProposalResponseSchema.parse({
        message: modelProposal.message?.trim() || summary,
        proposal: {
            summary,
            workflow,
            nodes,
            warnings: modelProposal.proposal.warnings ?? [],
        },
    });
}

export function normalizeAgentWorkflow(candidate: Workflow, currentWorkflow: Workflow): Workflow {
    const parsed = workflowSchema.parse({
        ...candidate,
        id: currentWorkflow.id,
        version: 1,
        name: candidate.name?.trim() || currentWorkflow.name,
    });

    if (!parsed.nodes.some((node) => node.kind === 'trigger-run')) {
        throw new Error('Generated workflow must include at least one trigger-run node.');
    }
    if (!parsed.nodes.some((node) => node.kind === 'output')) {
        throw new Error('Generated workflow must include at least one output node.');
    }

    const originalIds = new Set<string>();
    for (const node of parsed.nodes) {
        if (originalIds.has(node.id)) {
            throw new Error(`Generated workflow contains duplicate node id "${node.id}".`);
        }
        originalIds.add(node.id);
    }

    const usedNodeIds = new Set<string>();
    const idMap = new Map<string, string>();
    const nodes = parsed.nodes.map((node, index) => {
        const safeId = uniqueSafeId(sanitizeId(node.id) || defaultNodeId(node, index), usedNodeIds);
        idMap.set(node.id, safeId);
        return { ...node, id: safeId };
    });

    const usedEdgeIds = new Set<string>();
    const edges = parsed.edges.map((edge, index) => {
        const source = idMap.get(edge.source);
        const target = idMap.get(edge.target);
        if (!source || !target) {
            throw new Error(`Generated edge "${edge.id}" references a missing node.`);
        }
        if (source === target) {
            throw new Error(`Generated edge "${edge.id}" connects a node to itself.`);
        }
        const fallbackId = `e_${source}_${target}_${index + 1}`;
        return {
            id: uniqueSafeId(sanitizeId(edge.id) || sanitizeId(fallbackId), usedEdgeIds),
            source,
            target,
        };
    });

    const workflow = workflowSchema.parse({
        ...parsed,
        id: currentWorkflow.id,
        nodes,
        edges,
    });
    return workflowSchema.parse(layoutWorkflow(workflow));
}

/**
 * Pure-data ontology context the server injects into the system prompt. The
 * normaliser never imports the ontology — the server enumerates the readable
 * Object Types + the named-agent function catalog and passes them in here so
 * the builder model can wire `ontology-source` / `agent-function` nodes against
 * REAL firm data sources rather than asking the user to upload files.
 */
export interface WorkflowBuilderContext {
    objectTypes: Array<{ key: string; summary: string }>;
    agentFunctions: Array<{
        agentCode: string;
        functionKey: string;
        label: string;
        description: string;
    }>;
}

/**
 * System prompt for the workflow-builder model. Exposed so the server's
 * injected `callModel` can reuse the exact prompt the prototype used.
 *
 * When `context` is supplied, two sections are appended advertising the firm's
 * readable ontology data sources and the named-agent read functions, steering
 * the model toward `ontology-source` / `agent-function` nodes over file upload.
 */
export function buildSystemPrompt(context?: WorkflowBuilderContext): string {
    const lines = [
        'You are Septima Workflow Builder.',
        'Return strict JSON only. Do not wrap the JSON in Markdown.',
        'Always write workflow names, node labels, descriptions, placeholders, and system prompts in English.',
        'Use only these node kinds: trigger-run, user-question, ontology-source, agent-function, ollama-agent, anthropic-agent, output.',
        'The graph must be a directed acyclic workflow with at least one trigger-run node and one output node.',
        'Prefer a simple left-to-right flow. Do not invent unsupported fields or node kinds.',
    ];

    if (context) {
        lines.push(
            '',
            'Available ontology data sources (use an `ontology-source` node with one of these objectTypeKeys to read firm data — PREFER this over asking the user to upload files):',
            ...context.objectTypes.map((type) => `- ${type.key}: ${type.summary}`),
            '',
            'Available named-agent functions (use an `agent-function` node with the agentCode + functionKey):',
            ...context.agentFunctions.map(
                (fn) => `- ${fn.agentCode}/${fn.functionKey}: ${fn.label} — ${fn.description}`,
            ),
        );
    }

    lines.push(
        'Response shape:',
        JSON.stringify({
            message: 'Short assistant message',
            proposal: {
                summary: 'Short preview summary',
                warnings: [],
                workflow: {
                    id: 'preserve-current-id',
                    name: 'English workflow name',
                    version: 1,
                    nodes: [],
                    edges: [],
                },
            },
        }),
    );

    return lines.join('\n');
}

/**
 * Builds the user message for one proposal attempt. `validationError` is fed
 * back into the prompt on the repair attempt. Exposed for the server's
 * injected `callModel`.
 */
export function buildUserMessage(
    request: WorkflowAgentProposalRequest,
    validationError: string,
): string {
    const retry = validationError
        ? `\nYour previous JSON was rejected for this reason: ${validationError}\nReturn a corrected JSON object.`
        : '';

    return [
        `User request: ${request.prompt}`,
        'Current workflow JSON:',
        JSON.stringify(request.workflow, null, 2),
        'Generate a full replacement workflow proposal for the same workflow id.',
        retry,
    ].join('\n\n');
}

function extractJsonObject(raw: string): string {
    const trimmed = raw.trim();
    const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
    const text = fenced ? fenced[1]!.trim() : trimmed;
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start < 0 || end < start) {
        throw new Error('Model output did not contain a JSON object.');
    }
    return text.slice(start, end + 1);
}

function sanitizeId(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80);
}

function uniqueSafeId(base: string, used: Set<string>): string {
    const root = base || 'id';
    let next = root;
    let index = 2;
    while (used.has(next)) {
        next = `${root}_${index}`;
        index += 1;
    }
    used.add(next);
    return next;
}

function defaultNodeId(node: WorkflowNode, index: number): string {
    return `n_${node.kind.replace(/-/g, '_')}_${index + 1}`;
}

function layoutWorkflow(workflow: Workflow): Workflow {
    const order = topoOrder(workflow);
    const byId = new Map(workflow.nodes.map((node) => [node.id, node]));
    const incoming = new Map<string, string[]>();
    for (const node of workflow.nodes) incoming.set(node.id, []);
    for (const edge of workflow.edges) incoming.get(edge.target)?.push(edge.source);

    const levelById = new Map<string, number>();
    for (const id of order) {
        const level = Math.max(
            0,
            ...(incoming.get(id) ?? []).map((source) => (levelById.get(source) ?? 0) + 1),
        );
        levelById.set(id, level);
    }

    const rowsByLevel = new Map<number, string[]>();
    for (const id of order) {
        const level = levelById.get(id) ?? 0;
        rowsByLevel.set(level, [...(rowsByLevel.get(level) ?? []), id]);
    }

    const nodes = workflow.nodes.map((node) => {
        const level = levelById.get(node.id) ?? 0;
        const row = rowsByLevel.get(level)?.indexOf(node.id) ?? 0;
        return {
            ...byId.get(node.id)!,
            position: {
                x: 60 + level * 420,
                y: 260 + row * 280,
            },
        };
    });

    return { ...workflow, nodes };
}

function topoOrder(workflow: Workflow): string[] {
    const indegree = new Map<string, number>();
    const adjacent = new Map<string, string[]>();
    for (const node of workflow.nodes) {
        indegree.set(node.id, 0);
        adjacent.set(node.id, []);
    }
    for (const edge of workflow.edges) {
        if (!indegree.has(edge.target) || !adjacent.has(edge.source)) {
            throw new Error(`Generated edge "${edge.id}" references a missing node.`);
        }
        adjacent.get(edge.source)!.push(edge.target);
        indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
    }

    const queue = [...workflow.nodes.map((node) => node.id).filter((id) => indegree.get(id) === 0)];
    const order: string[] = [];
    while (queue.length) {
        const id = queue.shift()!;
        order.push(id);
        for (const target of adjacent.get(id) ?? []) {
            const next = (indegree.get(target) ?? 0) - 1;
            indegree.set(target, next);
            if (next === 0) queue.push(target);
        }
    }

    if (order.length !== workflow.nodes.length) {
        throw new Error('Generated workflow contains a cycle.');
    }
    return order;
}
